import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Avatar
} from '@mui/material';
import { AddIcon } from 'icons/materialIcons';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import DarkButtonWithIcon from 'components/common/button/DarkButtonWithIcon';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';
import { LEAD_FIELDS } from './LeadFieldMappings';
import avatarPlaceholder from 'images/avatar-cropped.png';

const CSVReview = forwardRef(({
  csvData,
  columns,
  initialMappings = {},
  onComplete,
  onCancel,
  userCards = [],
  selectedCardId = '',
  setSelectedCardId = () => { },
  loadingCards = false
}, ref) => {
  const [mappings, setMappings] = useState(initialMappings || {});
  const [checkedFields, setCheckedFields] = useState(
    Object.keys(LEAD_FIELDS).reduce((acc, key) => {
      acc[key] = key === 'leadEmail';
      return acc;
    }, {})
  );
  const [errors, setErrors] = useState([]);
  const [validatedData, setValidatedData] = useState([]);
  const [sampleData, setSampleData] = useState({});
  const [customFields, setCustomFields] = useState([{ name: '', value: '' }]);
  const [notificationOpen, setNotificationOpen] = useState(false);

  // Update sample data whenever mappings change
  useEffect(() => {
    if (csvData && csvData.length > 0) {
      const firstRow = csvData[0];
      const samples = {};

      Object.entries(mappings).forEach(([field, column]) => {
        if (column && firstRow) {
          samples[field] = firstRow[column];
        } else {
          samples[field] = "N/A";
        }
      });

      // Add custom fields
      customFields.forEach((field, index) => {
        const fieldKey = `custom_${index}`;
        if (mappings[fieldKey] && firstRow) {
          samples[fieldKey] = firstRow[mappings[fieldKey]];
        } else {
          samples[fieldKey] = "N/A";
        }
      });

      setSampleData(samples);
    }
  }, [mappings, csvData, customFields]);

  // Validation effect
  useEffect(() => {
    validateData();
  }, [mappings, customFields]);

  const validateData = () => {
    const newErrors = [];
    const validData = [];

    if (!csvData || !csvData.length) {
      setErrors(['No data to validate']);
      setValidatedData([]);
      return { errors: ['No data to validate'], validatedData: [] };
    }

    csvData.forEach((row, index) => {
      const validatedRow = {};
      let rowHasError = false;

      // Check for required email field first
      if (mappings.leadEmail) {
        const emailValue = row[mappings.leadEmail];
        if (!emailValue || (typeof emailValue === 'string' && emailValue.trim() === '')) {
          newErrors.push(`Row ${index + 1}: Missing required Email value`);
          rowHasError = true;
        }
      }

      // Process all standard fields based on mappings
      Object.entries(mappings).forEach(([field, column]) => {
        if (column) {
          // Skip custom fields, they'll be handled separately
          if (!field.startsWith('custom_')) {
            const value = row[column];

            const fieldConfig = LEAD_FIELDS[field] || { required: false };

            // Additional validation for required fields
            if (fieldConfig.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
              newErrors.push(`Row ${index + 1}: Missing required value for ${fieldConfig.label || field}`);
              rowHasError = true;
            }

            // Format validation if available
            if (fieldConfig.validate && value && !fieldConfig.validate(value)) {
              newErrors.push(`Row ${index + 1}: Invalid format for ${fieldConfig.label || field}`);
              rowHasError = true;
            }

            validatedRow[field] = value;
          }
        }
      });

      // Process custom fields
      const customFieldsObject = {};

      customFields.forEach((field, index) => {
        const fieldKey = `custom_${index}`;
        const column = mappings[fieldKey];

        if (column && field.name) {
          const value = row[column];

          // Add to custom fields object with the user-defined name as the key
          customFieldsObject[field.name] = {
            label: field.name,
            value: value || '',
            isRequired: false
          };
        }
      });

      // Add custom fields to validated row if any exist
      if (Object.keys(customFieldsObject).length > 0) {
        validatedRow.customFields = customFieldsObject;
      }

      if (!rowHasError) {
        validData.push(validatedRow);
      }
    });

    setErrors(newErrors);
    setValidatedData(validData);
    return { errors: newErrors, validatedData: validData };
  };

  const handleMappingChange = (field, column) => {
    setMappings(prev => ({
      ...prev,
      [field]: column
    }));

    // If a column is selected, automatically check the field
    if (column) {
      setCheckedFields(prev => ({
        ...prev,
        [field]: true
      }));
    } else {
      if (field !== 'leadEmail') {
        setCheckedFields(prev => ({
          ...prev,
          [field]: false
        }));
      }
    }
  };

  const handleCheckboxChange = (field) => {
    // Don't allow toggling for required fields
    if (field === 'leadEmail') return;

    // If the field is being checked but has no mapping, show notification
    if (!checkedFields[field] && !mappings[field]) {
      setNotificationOpen(true);
      return;
    }

    setCheckedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));

    // If unchecking, clear the mapping
    if (checkedFields[field]) {
      setMappings(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleNotificationClose = () => {
    setNotificationOpen(false);
  };

  const getAvailableColumns = (field) => {
    const usedColumns = Object.values(mappings).filter(column => column && column !== mappings[field]);
    return columns.filter(column => !usedColumns.includes(column));
  };

  const handleComplete = () => {
    // First check if email field is mapped - required
    if (!mappings.leadEmail) {
      setErrors(['Email field mapping is required']);
      alert('Please map the Email field - it is required for import');
      return;
    }

    // Check if custom fields have names
    const hasUnnamedCustomFields = customFields.some((field, index) => {
      const fieldKey = `custom_${index}`;
      return mappings[fieldKey] && !field.name;
    });

    if (hasUnnamedCustomFields) {
      alert('Please provide names for all mapped custom fields');
      return;
    }

    // Validate data and get results immediately
    const { errors: validationErrors, validatedData: validatedDataResult } = validateData();

    // If there are validation errors, show the first few
    if (validationErrors.length > 0) {
      const errorDisplay = validationErrors.slice(0, 3).join('\n') +
        (validationErrors.length > 3 ? `\n...and ${validationErrors.length - 3} more errors` : '');

      alert(`Please fix the following errors before importing:\n${errorDisplay}`);
      return;
    }

    // Check if we have valid data after validation
    if (validatedDataResult.length === 0) {
      alert('No valid data to import. Please check your file and try again.');
      return;
    }

    // If we get here, we're good to proceed with the import
    onComplete(validatedDataResult);
  };

  // Expose handleComplete method to parent via ref
  useImperativeHandle(ref, () => ({
    validateAndComplete: handleComplete
  }));

  const addCustomField = () => {
    setCustomFields([...customFields, { name: '', value: '' }]);
  };

  const handleCustomFieldNameChange = (index, value) => {
    const newCustomFields = [...customFields];
    newCustomFields[index].name = value;
    setCustomFields(newCustomFields);
  };

  // Order the fields in a specific sequence
  const orderedFields = [
    'leadEmail',
    'leadName',
    'leadJobTitle',
    'leadCompany',
    'leadPhoneNumber',
    'website',
    'leadAddress',
    'leadNotes'
  ];

  // Check if required email field is mapped
  const isEmailMapped = !!mappings.leadEmail;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper elevation={0} sx={{ p: 2, bgcolor: '#f9f9f9', maxHeight: '500px', overflowY: 'auto' }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Lead Info
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#828282' }}>
          Select which columns to include and choose how to map them
        </Typography>

        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
              <TableCell sx={{ border: 'none' }}>Field</TableCell>
              <TableCell sx={{ border: 'none' }}>Example</TableCell>
              <TableCell sx={{ border: 'none' }}>Map to CSV Column</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderedFields.map((key) => {
              const config = LEAD_FIELDS[key];
              return (
                <TableRow key={key}>
                  <TableCell sx={{ border: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkedFields[key]}
                            onChange={() => handleCheckboxChange(key)}
                            disabled={key === 'leadEmail'} // Email is always required
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {config.label === 'Email of Lead' ? 'Email (Required)' : config.label}
                          </Typography>
                        }
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: !mappings[key] ? 'italic' : 'normal',
                        color: !mappings[key] ? '#9e9e9e' : 'inherit'
                      }}
                    >
                      {sampleData[key] || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <Select
                        value={mappings[key] || ''}
                        onChange={(e) => handleMappingChange(key, e.target.value)}
                        displayEmpty
                        sx={{ fontSize: '14px' }}
                      >
                        <MenuItem value="" disabled>
                          <em>Unmapped</em>
                        </MenuItem>
                        {getAvailableColumns(key).map((column) => (
                          <MenuItem key={column} value={column}>
                            {column}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              );
            })}

            {customFields.map((field, index) => {
              const fieldKey = `custom_${index}`;
              return (
                <TableRow key={fieldKey}>
                  <TableCell sx={{ border: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkedFields[fieldKey] || false}
                            onChange={() => handleCheckboxChange(fieldKey)}
                          />
                        }
                        label={
                          <TextField
                            value={field.name}
                            onChange={(e) => handleCustomFieldNameChange(index, e.target.value)}
                            placeholder="Field Name"
                            size="small"
                            variant="outlined"
                            sx={{
                              ml: 1,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                backgroundColor: 'white'
                              }
                            }}
                          />
                        }
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: !mappings[fieldKey] ? 'italic' : 'normal',
                        color: !mappings[fieldKey] ? '#9e9e9e' : 'inherit'
                      }}
                    >
                      {sampleData[fieldKey] || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <Select
                        value={mappings[fieldKey] || ''}
                        onChange={(e) => handleMappingChange(fieldKey, e.target.value)}
                        displayEmpty
                        sx={{ fontSize: '14px' }}
                      >
                        <MenuItem value="" disabled>
                          <em>Unmapped</em>
                        </MenuItem>
                        {getAvailableColumns(fieldKey).map((column) => (
                          <MenuItem key={column} value={column}>
                            {column}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              );
            })}

            <TableRow>
              <TableCell colSpan={3} align="left" sx={{ border: 'none' }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addCustomField}
                  disabled={Object.keys(mappings).length < columns.length}
                  sx={{
                    color: Object.keys(mappings).length < columns.length ? '#C0C0C0' : '#8a8684',
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 600,
                    mt: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: Object.keys(mappings).length < columns.length ? '#C0C0C0' : '#29AEF8'
                    }
                  }}
                >
                  Add Field
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      <NotificationSnackbar
        open={notificationOpen}
        message="Select a field mapping first to include this value in the import"
        handleClose={handleNotificationClose}
        severity="warning"
      />
    </Box>
  );
});

export default CSVReview;